import { checkSession } from "@features/auth";
import { useI18nContext } from "@i18n/i18n-react";
import { preloadQuery } from "@relay";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@shadcn/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { useHubSession } from "@contexts/HubSessionContext";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { PackageOpen } from "lucide-react";
import { useRef, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { B2CCheckoutSummary } from "~/components/lock/B2CCheckout/B2CCheckoutSummary";
import { B2CSessionCheckout } from "~/components/lock/B2CCheckout/B2CSessionCheckout";
import { calculateB2CCheckoutPricing, formatB2CPrice, getB2CPricingEstimateDates } from "~/components/lock/B2CCheckout/pricing";
import { formatDimensions, getSizeBucket } from "~/components/lock/spaceConfiguration";
import { HubConnectionDrawer } from "~/components/HubConnectionDrawer";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { lockB2CIndexQuery } from "./__generated__/lockB2CIndexQuery.graphql";

type SpaceCategory = lockB2CIndexQuery["response"]["hubMeta"]["hub"]["availableConfigurations"][number]["spaces"][number]["category"];
const spaceCategories = ["XS", "S", "M", "L", "XL", "XXL"] as const;
type SupportedSpaceCategory = (typeof spaceCategories)[number];

function isSupportedSpaceCategory(category: SpaceCategory): category is SupportedSpaceCategory {
	return spaceCategories.some((supported) => supported === category);
}

const query = graphql`
	query lockB2CIndexQuery($hubName: String!, $oneHour: DateTime!, $twoHours: DateTime!, $oneDay: DateTime!, $oneWeek: DateTime!)
	@relay_test_operation {
		oneHourEstimate: b2CLockSessionFeeEstimate(input: { until: $oneHour })
		twoHoursEstimate: b2CLockSessionFeeEstimate(input: { until: $twoHours })
		oneDayEstimate: b2CLockSessionFeeEstimate(input: { until: $oneDay })
		oneWeekEstimate: b2CLockSessionFeeEstimate(input: { until: $oneWeek })
		holdXS: b2CLockInitialHold(category: XS)
		holdS: b2CLockInitialHold(category: S)
		holdM: b2CLockInitialHold(category: M)
		holdL: b2CLockInitialHold(category: L)
		holdXL: b2CLockInitialHold(category: XL)
		holdXXL: b2CLockInitialHold(category: XXL)
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			lat
			lng
			street
			postalCode
			city
			hub @required(action: THROW) {
				id
				status
				availableConfigurations {
					numSpaces
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					spaces {
						id
						category
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/lock/new")({
	beforeLoad: () => {
		document.title = "bacehub | Rent a locker";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ category: z.enum(spaceCategories).optional(), session: z.string().uuid().optional() })),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/lock/new${ctx.location.searchStr}` },
			});
		}

		return preloadQuery<lockB2CIndexQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			...getB2CPricingEstimateDates(),
		});
	},
});

function RouteComponent() {
	const search = Route.useSearch();
	const data = usePreloadedQuery<lockB2CIndexQuery>(query, Route.useLoaderData());
	const { hubMeta, oneHourEstimate, twoHoursEstimate, oneDayEstimate, oneWeekEstimate } = data;

	if (search.session) return <B2CSessionCheckout sessionId={search.session} hubMetaName={hubMeta.name} />;
	if (search.category) {
		const initialHold = {
			XS: data.holdXS,
			S: data.holdS,
			M: data.holdM,
			L: data.holdL,
			XL: data.holdXL,
			XXL: data.holdXXL,
		}[search.category];
		return (
			<B2CCheckoutSummary
				category={search.category}
				initialHold={Number(initialHold)}
				pricing={calculateB2CCheckoutPricing(search.category, {
					oneHour: oneHourEstimate,
					twoHours: twoHoursEstimate,
					oneDay: oneDayEstimate,
					oneWeek: oneWeekEstimate,
				})}
				hub={{
					id: hubMeta.hub.id,
					name: hubMeta.name,
					street: hubMeta.street,
					postalCode: hubMeta.postalCode,
					city: hubMeta.city,
				}}
			/>
		);
	}

	return <CompartmentSelection />;
}

function CompartmentSelection() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { hubMeta, oneHourEstimate, twoHoursEstimate, oneDayEstimate, oneWeekEstimate } = usePreloadedQuery<lockB2CIndexQuery>(
		query,
		Route.useLoaderData()
	);
	const { hub: connectedHub } = useHubSession();
	const isHubOnline = hubMeta.hub.status === "ONLINE";
	const [selectedCategory, setSelectedCategory] = useState<SupportedSpaceCategory | "ALL">("ALL");
	const pendingCategoryRef = useRef<SupportedSpaceCategory | null>(null);
	const [connectDrawerOpen, setConnectDrawerOpen] = useState(false);
	const categories = new Map<
		SupportedSpaceCategory,
		{
			category: SupportedSpaceCategory;
			configuration: string;
			dimensions: { widthMm: number; heightMm: number; depthMm: number };
			available: number;
		}
	>();

	for (const configuration of hubMeta.hub.availableConfigurations) {
		const spaceCategory = configuration.spaces[0]?.category;
		const sizeBucket = getSizeBucket(configuration.configuration);
		const category = spaceCategory ?? (sizeBucket === "2XL" ? "XXL" : sizeBucket);
		if (!category || !isSupportedSpaceCategory(category)) continue;

		const current = categories.get(category);
		if (current) {
			current.available += configuration.numSpaces;
			continue;
		}

		categories.set(category, {
			category,
			configuration: configuration.configuration,
			dimensions: configuration.dimensions,
			available: configuration.numSpaces,
		});
	}

	const options = spaceCategories
		.flatMap((category) => {
			const option = categories.get(category);
			return option ? [option] : [];
		})
		.sort((a, b) => Number(a.available === 0) - Number(b.available === 0));
	const visibleOptions = selectedCategory === "ALL" ? options : options.filter((option) => option.category === selectedCategory);
	const availableCategories = options.map((option) => option.category);
	const continueToCheckout = (category: SupportedSpaceCategory) =>
		navigate({
			to: "/$hubMetaName/lock/new",
			params: { hubMetaName: hubMeta.name },
			search: { category },
		});
	const chooseCompartment = (category: SupportedSpaceCategory) => {
		if (connectedHub?.id === hubMeta.hub.id) {
			void continueToCheckout(category);
			return;
		}

		pendingCategoryRef.current = category;
		setConnectDrawerOpen(true);
	};

	return (
		<>
			<PageContainer className="min-h-dvh bg-slate-50">
				<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2C" />
				<main className="mx-auto flex w-full max-w-[390px] flex-col gap-6 px-6 pb-8 pt-6">
					<header>
						<h1 className="text-2xl font-breit font-medium leading-8">{i18n.LOCK_RENT_TITLE()}</h1>
					</header>
					{!isHubOnline ? (
						<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
							{i18n.LOCK_RENT_HUB_OFFLINE()}
						</p>
					) : null}

					{options.length === 0 ? (
						<Card className="border-dashed bg-background">
							<CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
								<span className="flex size-12 items-center justify-center rounded-full bg-muted" aria-hidden="true">
									<PackageOpen className="size-6 text-muted-foreground" />
								</span>
								<h2 className="text-lg font-semibold">No compartments available</h2>
								<p className="max-w-md text-sm leading-6 text-muted-foreground">
									There are no compartments available to rent at this location right now. Please check again later.
								</p>
							</CardContent>
						</Card>
					) : (
						<>
							<Tabs value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as SupportedSpaceCategory | "ALL")}>
								<TabsList className="grid h-9 w-full grid-flow-col auto-cols-fr rounded-[10px] p-[3px]">
									<TabsTrigger value="ALL" className="rounded-lg px-2 font-breit">
										{i18n.LOCK_RENT_FILTER_ALL()}
									</TabsTrigger>
									{availableCategories.map((category) => (
										<TabsTrigger key={category} value={category} className="rounded-lg px-2 font-breit">
											{category}
										</TabsTrigger>
									))}
								</TabsList>
							</Tabs>

							<section aria-live="polite" aria-label="Available compartment sizes" className="flex flex-col gap-6">
								{visibleOptions.map((option) => {
									const isAvailable = isHubOnline && option.available > 0;
									const pricing = calculateB2CCheckoutPricing(option.category, {
										oneHour: oneHourEstimate,
										twoHours: twoHoursEstimate,
										oneDay: oneDayEstimate,
										oneWeek: oneWeekEstimate,
									});
									return (
										<Card
											key={option.category}
											className={`gap-4 rounded-[14px] border bg-white p-6 shadow-md ${isAvailable ? "" : "opacity-50"}`}
										>
											<CardHeader className="flex-row items-start justify-between gap-2 p-0">
												<div className="flex min-w-0 flex-col gap-1">
													<CardTitle className="text-xl font-breit leading-none">{option.category}</CardTitle>
													<p className="text-base text-muted-foreground">{formatDimensions(option.dimensions)}</p>
												</div>
												<Badge
													className={`shrink-0 rounded-lg px-2 py-0.5 text-xs font-breit text-white ${
														option.available === 1 ? "bg-destructive" : "bg-neutral-900"
													}`}
												>
													{!isHubOnline
														? i18n.LOCK_HUB_OFFLINE()
														: !isAvailable
															? i18n.LOCK_RENT_NOT_AVAILABLE_BADGE()
															: option.available === 1
																? i18n.LOCK_RENT_ONLY_ONE_LEFT_BADGE()
																: i18n.LOCK_RENT_AVAILABLE_BADGE()}
												</Badge>
											</CardHeader>
											<CardContent className="flex flex-col gap-1 p-0">
												<PriceRow label={i18n.LOCK_PRICE_HOURLY_RATE()} value={formatB2CPrice(pricing.hourlyRate)} emphasized />
												<PriceRow label={i18n.LOCK_PRICE_DAILY_CAP()} value={formatB2CPrice(pricing.dailyCeiling)} />
												<PriceRow label={i18n.LOCK_PRICE_WEEKLY_CAP()} value={formatB2CPrice(pricing.weekCeiling)} />
												<PriceRow label={i18n.LOCK_PRICE_ONE_TIME_START_FEE()} value={formatB2CPrice(pricing.startFee)} />
											</CardContent>
											<CardFooter className="p-0">
												<Button
													className="h-10 w-full font-breit"
													disabled={!isAvailable}
													aria-label={i18n.LOCK_RENT_CHOOSE_ARIA({ category: option.category })}
													onClick={() => chooseCompartment(option.category)}
												>
													{i18n.LOCK_RENT_CHOOSE()}
												</Button>
											</CardFooter>
										</Card>
									);
								})}
							</section>
						</>
					)}
				</main>
			</PageContainer>
			<HubConnectionDrawer
				isOpen={connectDrawerOpen}
				onOpenChange={setConnectDrawerOpen}
				hubData={{
					id: hubMeta.hub.id,
					name: hubMeta.name,
					lat: hubMeta.lat,
					lng: hubMeta.lng,
				}}
				onHubConnected={() => {
					const category = pendingCategoryRef.current;
					pendingCategoryRef.current = null;
					if (category) void continueToCheckout(category);
				}}
			/>
		</>
	);
}

function PriceRow(props: { label: string; value: string; emphasized?: boolean }) {
	return (
		<div
			className={`flex items-center justify-between gap-4 ${
				props.emphasized ? "font-breit font-medium text-foreground" : "text-sm text-muted-foreground"
			}`}
		>
			<span>{props.label}</span>
			<span>{props.value}</span>
		</div>
	);
}
